<script setup lang="ts">
import { ref, watch } from 'vue'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'

import SingleSelect from '@/components/common/ui/inputs/SingleSelect.vue'

import type { GlassTab, GlassTabList } from './types'

interface GlassTabProps {
  tabList: GlassTabList
  wide?: boolean
}

const { tabList, wide = true } = defineProps<GlassTabProps>()

const selectedTabKeyModel = defineModel<string>('selectedTabKey')

const slots = defineSlots<{
  default: string
}>()

const isCurrentTab = (tab: GlassTab): boolean => {
  return tab.key === selectedTabKeyModel.value
}

const smallTabOptionList: DropdownOption<string>[] = tabList
  .filter((tab) => {
    return !(tab.disabled ?? false)
  })
  .map((tab) => {
    const mapDropdownOption: DropdownOption<string> = {
      optionLabel: tab.name,
      optionValue: tab.key,
    }

    return mapDropdownOption
  })

const smallTabSelectValue = ref(
  smallTabOptionList.find((option) => {
    return option.optionValue === selectedTabKeyModel.value
  })?.optionValue,
)

const onTabClick = (tab: GlassTab): void => {
  const isTabDisabled = tab.disabled ?? false

  if (!isTabDisabled) {
    selectedTabKeyModel.value = tab.key
  }
}

watch(
  () => {
    return selectedTabKeyModel.value
  },
  (newVal) => {
    smallTabSelectValue.value = smallTabOptionList.find((option) => {
      return option.optionValue === newVal
    })?.optionValue
  },
)

watch(
  () => {
    return smallTabSelectValue.value
  },
  (newVal) => {
    if (newVal) {
      selectedTabKeyModel.value = newVal
    }
  },
)

const screenReaderLabel = 'select a tab'
const navAriaLabel = 'Tabs'
</script>

<template>
  <div class="">
    <div class="@container/GlassTab">
      <div :class="[wide ? '@lg/GlassTab:hidden' : '@xs/GlassTab:hidden']">
        <label class="sr-only" for="tabs">
          {{ screenReaderLabel }}
        </label>
        <SingleSelect
          id="tabs"
          v-model:selected="smallTabSelectValue"
          :options="[smallTabOptionList]"
          placeholder=""
        />
      </div>
      <div class="hidden" :class="[wide ? '@lg/GlassTab:block' : '@xs/GlassTab:block']">
        <nav :aria-label="navAriaLabel" class="flex">
          <a
            v-for="(currentTab, currentTabIndex) in tabList"
            :key="currentTabIndex"
            :aria-current="isCurrentTab(currentTab) ? 'page' : false"
            class="group inline-flex flex-shrink-0 cursor-pointer items-center gap-1 border-b-2 px-3 py-2.5 text-sm"
            :class="[
              isCurrentTab(currentTab)
                ? 'border-b-lochmara-600 text-lochmara-600'
                : 'border-b-transparent',
              currentTab.disabled
                ? 'text-black-400 hover:cursor-not-allowed hover:text-black-400'
                : 'text-black-900 hover:text-lochmara-600',
            ]"
            :data-testid="currentTab.testid"
            @click="() => onTabClick(currentTab)"
          >
            <component :is="currentTab.icon" aria-hidden="true" class="size-6" />
            <span>
              {{ currentTab.name }}
            </span>
          </a>
        </nav>
      </div>
    </div>
    <div v-if="slots.default" class="">
      <slot></slot>
    </div>
  </div>
</template>
